import { pointsLabel } from "./format.js";
import { canalValido, type Canal } from "./compra.js";
import type {
  AcaoDeMissao, ChallengeConfig, ChallengeEvent, CondicaoDeMissao, DayKey, EventState, Plate,
} from "./types.js";

/* ============================================================
   Missões

   Uma missão é um objetivo com prazo e prêmio. Três tipos:

   - COLEÇÃO (o de sempre): junte N selos de cozinha DIFERENTES de uma
     lista. "Mês asiático: 3 cozinhas desta lista, +2."
   - CONTAGEM: poste N pratos que cumprem as condições. "Semana leve: 5
     pratos de até 600 kcal, +3."
   - SEQUÊNCIA: N dias SEGUIDOS com pelo menos um prato que cumpre as
     condições. "5 dias sem fritura, +3."

   POR QUE O CONTEÚDO DO PRATO SÓ DÁ PONTO DENTRO DE MISSÃO. A primeira
   versão (set/2026) dava bônus por prato a cada "selo automático" — sem
   açúcar, sem álcool, sem fritura… — e o Gustavo achou o furo na hora:
   um arroz, feijão e strogonoff comum não tem álcool, nem fritura, nem
   açúcar, e ganhava quatro bônus por ser um almoço normal. Premiar a
   AUSÊNCIA de algo só faz sentido quando é compromisso — "esta semana a
   gente está sem açúcar" —, e compromisso é missão: uma condição
   escolhida, um prazo, e o prêmio uma vez, por cumprir.

   Só conta prato que PONTUA: a segunda janta do dia não conta, senão
   cinco saladas no mesmo almoço fechavam a "semana leve" num dia.

   Condição de conteúdo (caloria, "sem X") depende da estimativa da IA:
   prato sem estimativa ainda não conta, e passa a contar quando ela
   chega — é a escrita dela que refaz o placar. A correção de calorias de
   quem postou vale (decisão do Gustavo, set/2026).
   ============================================================ */

/**
 * O que a IA diz que TEM no prato. Lista fechada: é o que a instrução do
 * modelo conhece, e uma condição só pode pedir o que ela sabe responder.
 */
export const MARCAS = [
  { id: "acucar", rotulo: "açúcar", descricao: "doce, sobremesa, refrigerante ou açúcar adicionado" },
  /* Batata palha e salgadinho entram pelo nome: no teste de set/2026 a IA
     marcou um strogonoff com batata palha como ultraprocessado e NÃO como
     fritura, e aí ele fechava a missão "sem fritura". */
  { id: "fritura", rotulo: "fritura", descricao: "frito em imersão: batata frita, batata palha, chips, pastel, coxinha, empanado, milanesa" },
  { id: "carne", rotulo: "carne", descricao: "qualquer carne, frango, peixe ou frutos do mar" },
  { id: "ultraprocessado", rotulo: "ultraprocessado", descricao: "embutido, salgadinho, fast food, industrializado" },
  { id: "alcool", rotulo: "álcool", descricao: "bebida alcoólica" },
  { id: "gluten", rotulo: "glúten", descricao: "trigo: pão, massa, bolo, empanado" },
  { id: "lactose", rotulo: "lactose", descricao: "leite, queijo, creme de leite, manteiga" },
] as const;

export type Marca = (typeof MARCAS)[number]["id"];

const IDS_DE_MARCA = new Set<string>(MARCAS.map((m) => m.id));

/** Filtra o que a IA mandou para o que a lista conhece. */
export function marcasValidas(valor: unknown): Marca[] | null {
  if (!Array.isArray(valor)) return null;
  return [...new Set(valor.filter((v): v is Marca => typeof v === "string" && IDS_DE_MARCA.has(v)))];
}

export type TipoDeMissao = NonNullable<ChallengeEvent["tipo"]>;

const TIPOS_VALIDOS = new Set<TipoDeMissao>([
  "contagem", "sequencia", "lugares", "diaCompleto", "atividade", "coletivaPratos", "coletivaTodoDia",
]);

export const tipoDaMissao = (e: Pick<ChallengeEvent, "tipo">): TipoDeMissao =>
  e.tipo && TIPOS_VALIDOS.has(e.tipo) ? e.tipo : "colecao";

/** Os tipos que olham o PRATO e aceitam condições. */
export const usaCondicoes = (e: Pick<ChallengeEvent, "tipo">): boolean =>
  !["colecao", "atividade"].includes(tipoDaMissao(e));

/** Missão do GRUPO: o prêmio vai para várias pessoas de uma vez. */
export const ehColetiva = (e: Pick<ChallengeEvent, "tipo">): boolean =>
  tipoDaMissao(e) === "coletivaPratos" || tipoDaMissao(e) === "coletivaTodoDia";

/**
 * A hora de Brasília em que o prato foi comido, 0–23.
 *
 * UTC−3 fixo, e não o fuso do aparelho: o motor roda no servidor, que não
 * sabe de onde a pessoa postou, e o Brasil não tem horário de verão desde
 * 2019. Quem posta de outro fuso vê a hora deslocada — é o preço de a
 * conta dar o mesmo número no servidor e na tela.
 */
export const horaDoPrato = (at: number): number =>
  (new Date(at).getUTCHours() + 21) % 24;

type PratoParaCondicao = Pick<Plate, "kcal" | "marcas" | "homemade" | "mealId"> &
  Partial<Pick<Plate, "cuisineId" | "price" | "canal" | "ratingAvg" | "ratingCount" | "lugarNovo" | "at" | "fechado">>;

/** O prato cumpre esta condição? */
export function cumpre(c: CondicaoDeMissao, plate: PratoParaCondicao): boolean {
  switch (c.tipo) {
    case "kcalAte": {
      const kcal = plate.kcal;
      // 0 é "a IA não achou comida": não é prato leve, é prato sem leitura.
      return typeof kcal === "number" && kcal > 0 && kcal <= c.kcal;
    }
    case "sem":
      // Sem avaliação não conta: "sem açúcar" por ninguém ter olhado seria de graça.
      return Array.isArray(plate.marcas) && !plate.marcas.includes(c.marca);
    case "cozinhado":
      return plate.homemade;
    case "comprado":
      return !plate.homemade;
    case "refeicao":
      return typeof plate.mealId === "string" && plate.mealId.trim() === c.mealId;
    case "cozinha":
      return plate.cuisineId === c.cuisineId;
    case "precoAte":
      // Sem preço declarado não conta: "barato" por não ter dito quanto seria de graça.
      return typeof plate.price === "number" && plate.price > 0 && plate.price <= c.valor;
    case "horaAntes":
      return typeof plate.at === "number" && horaDoPrato(plate.at) < c.hora;
    case "horaDepois": {
      if (typeof plate.at !== "number") return false;
      const h = horaDoPrato(plate.at);
      // Madrugada é noite: a larica das 1h conta como "depois das 23h".
      return h >= c.hora || (c.hora >= 18 && h < 5);
    }
    case "canal":
      return !!plate.canal && c.canais.includes(plate.canal);
    case "notaMin":
      // Duas notas ou mais: a nota 10 de um amigo sozinha não faz Masterchef.
      // Prato com notas desligadas não entra: ninguém mais pode dar nota nele.
      return !plate.fechado && typeof plate.ratingAvg === "number" && (plate.ratingCount ?? 0) >= 2
        && plate.ratingAvg >= c.nota;
    case "lugarNovo":
      return plate.lugarNovo === true;
  }
}

/** Todas as condições da missão (lista vazia = qualquer prato). */
export const cumpreTodas = (
  condicoes: readonly CondicaoDeMissao[] | undefined,
  plate: PratoParaCondicao,
): boolean => (condicoes ?? []).every((c) => cumpre(c, plate));

const reais = (v: number) =>
  `R$ ${Number.isInteger(v) ? v : v.toFixed(2).replace(".", ",")}`;

/** "até 600 kcal", "sem açúcar", "cozinhado", "no café". */
export function descreverCondicao(
  c: CondicaoDeMissao, config?: ChallengeConfig, plural = false,
): string {
  switch (c.tipo) {
    case "kcalAte": return `até ${c.kcal} kcal`;
    case "sem": return `sem ${MARCAS.find((m) => m.id === c.marca)?.rotulo ?? c.marca}`;
    case "cozinhado": return plural ? "cozinhados" : "cozinhado";
    case "comprado": return plural ? "comprados" : "comprado";
    case "refeicao": {
      const nome = config?.meals.find((m) => m.id === c.mealId)?.label ?? c.mealId;
      return `${artigoDaRefeicao(nome)} ${nome.toLowerCase()}`;
    }
    case "cozinha": {
      const cz = config?.cuisines.find((x) => x.id === c.cuisineId);
      return `de cozinha ${(cz?.label ?? c.cuisineId).toLowerCase()}`;
    }
    case "precoAte": return `de até ${reais(c.valor)}`;
    case "horaAntes": return `antes das ${c.hora}h`;
    case "horaDepois": return `depois das ${c.hora}h`;
    case "canal": {
      const nomes: Record<string, string> = { local: "no local", retirada: "retirada", delivery: "delivery" };
      return c.canais.map((x) => nomes[x] ?? x).join(" ou ");
    }
    case "notaMin": return `com nota ${c.nota} ou mais`;
    case "lugarNovo": return "em lugar que ninguém do grupo marcou";
  }
}

/** "no café", "na janta": o rótulo é livre, então vai pela última letra. */
export const artigoDaRefeicao = (rotulo: string): "no" | "na" =>
  /a$/i.test(rotulo.trim()) ? "na" : "no";

/** O que a missão pede, numa frase: "5 pratos até 600 kcal". */
export function descreverMissao(e: ChallengeEvent, config?: ChallengeConfig): string {
  const n = Math.max(1, e.need);
  const tipo = tipoDaMissao(e);
  const plural = n > 1 && (tipo === "contagem" || tipo === "coletivaPratos");
  const cond = (e.condicoes ?? []).map((c) => descreverCondicao(c, config, plural)).join(", ");
  const com = cond ? ` ${cond}` : "";
  switch (tipo) {
    case "contagem":
      return `${n} ${n === 1 ? "prato" : "pratos"}${com}`;
    case "sequencia":
      return `${n} dias seguidos${cond ? ` com prato ${cond}` : " postando"}`;
    case "lugares":
      return `${n} ${n === 1 ? "lugar" : "lugares diferentes"}${cond ? `, com prato ${cond}` : ""}`;
    case "diaCompleto": {
      const nomes = (e.refeicoes ?? [])
        .map((id) => (config?.meals.find((m) => m.id === id)?.label ?? id).toLowerCase());
      const lista = nomes.length > 1
        ? `${nomes.slice(0, -1).join(", ")} e ${nomes.at(-1)}` : nomes[0] ?? "todas as refeições";
      return `${n} ${n === 1 ? "dia" : "dias"} com ${lista} no mesmo dia`;
    }
    case "atividade":
      return e.acao === "comentar"
        ? `comentar em ${n} ${n === 1 ? "prato" : "pratos"} dos outros`
        : `cravar ${n} ${n === 1 ? "palpite" : "palpites"} de preço ou caloria`;
    case "coletivaPratos":
      return `o grupo posta ${n} ${n === 1 ? "prato" : "pratos"}${com} — quem contribuiu ganha`;
    case "coletivaTodoDia":
      return `todo mundo posta${cond ? ` prato ${cond}` : ""} ${n} dias seguidos — se um furar, ninguém ganha`;
    default:
      return `${n} ${n === 1 ? "selo" : "selos"} diferentes da lista`;
  }
}

/** Condições que dependem da IA: sem estimativa, o prato ainda não conta. */
export const dependeDaIA = (e: Pick<ChallengeEvent, "condicoes">): boolean =>
  (e.condicoes ?? []).some((c) => c.tipo === "kcalAte" || c.tipo === "sem");

/* ---------- o cálculo ---------- */

function diaSeguinte(dia: DayKey): DayKey {
  const [y, m, d] = dia.split("-").map(Number);
  const data = new Date(Date.UTC(y ?? 1970, (m ?? 1) - 1, (d ?? 1) + 1));
  const p = (n: number) => String(n).padStart(2, "0");
  return `${data.getUTCFullYear()}-${p(data.getUTCMonth() + 1)}-${p(data.getUTCDate())}`;
}

/**
 * Os pratos que PONTUAM, em ordem: tira a segunda refeição igual do dia,
 * pela mesma regra de `scoreMember`. Por pessoa: a mesma refeição de duas
 * pessoas no mesmo dia não é repetição.
 */
export function pratosQuePontuam(plates: readonly Plate[]): Plate[] {
  const ordem = [...plates].sort((a, b) => (a.day < b.day ? -1 : a.day > b.day ? 1 : a.at - b.at));
  const vistos = new Set<string>();
  return ordem.filter((p) => {
    if (!p.mealId) return true;
    const chave = `${p.uid}|${p.day}|${p.mealId}`;
    if (vistos.has(chave)) return false;
    vistos.add(chave);
    return true;
  });
}

const aberta = (e: ChallengeEvent, dia: DayKey) =>
  !(e.from && dia < e.from) && !(e.to && dia > e.to);

/**
 * O que o motor precisa além dos pratos da pessoa. Tudo opcional: sem
 * isto, as missões de atividade e as coletivas simplesmente não andam
 * (é o caso das telas, que leem o estado já gravado pelo servidor).
 */
export interface ContextoDeMissao {
  /** Quem é a pessoa: é o que diz se ela contribuiu numa coletiva. */
  uid?: string;
  /** Cravadas e comentários em prato ALHEIO, por dia. Escrito pelo servidor. */
  atividade?: Partial<Record<AcaoDeMissao, Record<DayKey, number>>>;
  /** Os pratos do GRUPO todo na janela, e quem são os membros. */
  grupo?: { plates: readonly Plate[]; membros: readonly string[] };
}

const fechado = (event: ChallengeEvent, dia: DayKey | "") => ({
  bonus: dia ? event.bonus : 0, day: dia,
});

/** Corrida de dias seguidos numa lista ordenada de dias: a atual e onde fechou. */
function corridaDeDias(dias: readonly DayKey[], need: number): { corrida: number; ultimo: DayKey | ""; fechou: DayKey | "" } {
  let corrida = 0;
  let anterior = "";
  let fechou: DayKey | "" = "";
  for (const dia of dias) {
    corrida = anterior && diaSeguinte(anterior) === dia ? corrida + 1 : 1;
    anterior = dia;
    if (corrida >= need && !fechou) fechou = dia;
  }
  return { corrida, ultimo: anterior, fechou };
}

/**
 * Progresso de uma missão.
 * Recalculado do zero de propósito: se a pessoa apagar o prato que fechou
 * a missão, o bônus tem que sumir junto.
 */
export function eventStateFor(
  event: ChallengeEvent,
  plates: readonly Plate[],
  contexto: ContextoDeMissao = {},
): EventState {
  const estado = estadoSemExclusao(event, plates, contexto);
  /* Tirada da missão: o progresso continua visível (é o do grupo, nas
     coletivas), mas o bônus não entra. */
  if (contexto.uid && event.fora?.includes(contexto.uid)) {
    return { ...estado, bonus: 0, day: "", fora: true };
  }
  return estado;
}

function estadoSemExclusao(
  event: ChallengeEvent,
  plates: readonly Plate[],
  contexto: ContextoDeMissao,
): EventState {
  const need = Math.max(1, event.need);
  const tipo = tipoDaMissao(event);
  if (tipo === "colecao") {
    /* TODOS os pratos, repetidos inclusive — é como a coleção sempre
       contou, e mudar agora mexeria no placar de missões já fechadas. */
    const validos = [...plates]
      .sort((a, b) => (a.day < b.day ? -1 : a.day > b.day ? 1 : a.at - b.at))
      .filter((p) => aberta(event, p.day));
    const alvo = new Set(event.cuisineIds ?? []);
    const coletados = new Set<string>();
    let fechou: DayKey | "" = "";
    for (const p of validos) {
      if (!p.cuisineId || !alvo.has(p.cuisineId) || coletados.has(p.cuisineId)) continue;
      coletados.add(p.cuisineId);
      if (coletados.size === need && !fechou) fechou = p.day;
    }
    return { cuisineIds: [...coletados], feitos: coletados.size, ultimo: "", ...fechado(event, fechou) };
  }

  if (tipo === "atividade") {
    const porDia = contexto.atividade?.[event.acao ?? "cravar"] ?? {};
    let soma = 0;
    let fechou: DayKey | "" = "";
    let ultimo: DayKey | "" = "";
    for (const dia of Object.keys(porDia).sort()) {
      if (!aberta(event, dia)) continue;
      const n = Math.max(0, Number(porDia[dia]) || 0);
      if (!n) continue;
      soma += n;
      ultimo = dia;
      if (soma >= need && !fechou) fechou = dia;
    }
    return { cuisineIds: [], feitos: soma, ultimo, ...fechado(event, fechou) };
  }

  if (tipo === "coletivaPratos" || tipo === "coletivaTodoDia") {
    return estadoColetivo(event, contexto);
  }

  const bons = pratosQuePontuam(plates)
    .filter((p) => aberta(event, p.day) && cumpreTodas(event.condicoes, p));

  if (tipo === "contagem") {
    const fechou = bons.length >= need ? bons[need - 1]!.day : "";
    return { cuisineIds: [], feitos: bons.length, ultimo: bons.at(-1)?.day ?? "", ...fechado(event, fechou) };
  }

  if (tipo === "lugares") {
    const vistos = new Set<string>();
    let fechou: DayKey | "" = "";
    for (const p of bons) {
      if (!p.placeKey || vistos.has(p.placeKey)) continue;
      vistos.add(p.placeKey);
      if (vistos.size === need && !fechou) fechou = p.day;
    }
    return { cuisineIds: [], feitos: vistos.size, ultimo: bons.at(-1)?.day ?? "", ...fechado(event, fechou) };
  }

  if (tipo === "diaCompleto") {
    const exigidas = event.refeicoes?.length ? event.refeicoes : [];
    const porDia = new Map<DayKey, Set<string>>();
    for (const p of bons) {
      if (!p.mealId) continue;
      const set = porDia.get(p.day) ?? new Set<string>();
      set.add(p.mealId);
      porDia.set(p.day, set);
    }
    const completos = [...porDia.entries()]
      .filter(([, feitas]) => exigidas.length > 0 && exigidas.every((m) => feitas.has(m)))
      .map(([dia]) => dia)
      .sort();
    const fechou = completos.length >= need ? completos[need - 1]! : "";
    return { cuisineIds: [], feitos: completos.length, ultimo: completos.at(-1) ?? "", ...fechado(event, fechou) };
  }

  // Sequência: os dias com prato bom, e a corrida de dias seguidos.
  const r = corridaDeDias([...new Set(bons.map((p) => p.day))].sort(), need);
  return {
    cuisineIds: [],
    /* A corrida que termina no último dia bom. Quem desenha decide se
       ela ainda está viva (último dia = hoje ou ontem) ou se quebrou. */
    feitos: r.corrida,
    ultimo: r.ultimo,
    ...fechado(event, r.fechou),
  };
}

/**
 * As coletivas: o progresso é do GRUPO, o prêmio é de cada um.
 *
 * - `coletivaPratos`: o grupo soma N pratos que cumprem; fecha no dia do
 *   N-ésimo, e ganha quem pôs pelo menos um prato até ali. Quem só postou
 *   depois de fechar não ganha — senão a missão viraria prêmio de graça
 *   para o último a chegar.
 * - `coletivaTodoDia`: N dias seguidos em que TODOS os membros postaram
 *   prato que cumpre. Fechou, todo mundo ganha; um furou, a corrida volta
 *   a zero para todos.
 *
 * `feitos` é o número do grupo; `meus` é o da pessoa (pratos que ela pôs,
 * ou, no "todo dia", se ela já postou no último dia contado).
 */
function estadoColetivo(event: ChallengeEvent, contexto: ContextoDeMissao): EventState {
  const need = Math.max(1, event.need);
  const grupo = contexto.grupo;
  const eu = contexto.uid ?? "";
  if (!grupo) return { cuisineIds: [], feitos: 0, ultimo: "", meus: 0, bonus: 0, day: "" };

  const fora = new Set(event.fora ?? []);
  const bons = pratosQuePontuam(grupo.plates)
    .filter((p) => !fora.has(p.uid) && aberta(event, p.day) && cumpreTodas(event.condicoes, p));

  if (tipoDaMissao(event) === "coletivaPratos") {
    const fechouEm = bons.length >= need ? bons[need - 1]! : null;
    const contribuintes = new Set((fechouEm ? bons.slice(0, need) : bons).map((p) => p.uid));
    const meus = bons.filter((p) => p.uid === eu).length;
    const ganhei = !!fechouEm && contribuintes.has(eu);
    return {
      cuisineIds: [], feitos: bons.length, ultimo: bons.at(-1)?.day ?? "", meus,
      bonus: ganhei ? event.bonus : 0, day: ganhei ? fechouEm!.day : "",
    };
  }

  /* Todo dia: um dia vale quando todos os membros ATIVOS naquele dia
     postaram. Ativo = postou qualquer prato nos 7 dias até ali. Sem isso,
     um membro que sumiu fazia o grupo nunca mais fechar a missão. Quem o
     dono tirou da missão também não conta. */
  const membros = new Set(grupo.membros.filter((u) => !fora.has(u)));
  const diasDe = new Map<string, DayKey[]>();
  for (const p of grupo.plates) {
    const l = diasDe.get(p.uid) ?? [];
    l.push(p.day);
    diasDe.set(p.uid, l);
  }
  const ativosEm = (dia: DayKey) => {
    const desde = somarDias(dia, -(DIAS_ATIVO_NA_COLETIVA - 1));
    return [...membros].filter((u) => (diasDe.get(u) ?? []).some((d) => d >= desde && d <= dia));
  };
  const porDia = new Map<DayKey, Set<string>>();
  for (const p of bons) {
    const set = porDia.get(p.day) ?? new Set<string>();
    set.add(p.uid);
    porDia.set(p.day, set);
  }
  const completos = [...porDia.entries()]
    .filter(([dia, quem]) => {
      const ativos = ativosEm(dia);
      return ativos.length > 0 && ativos.every((u) => quem.has(u));
    })
    .map(([dia]) => dia)
    .sort();
  const r = corridaDeDias(completos, need);
  const diasDaPessoa = [...porDia.entries()].filter(([, quem]) => quem.has(eu)).map(([d]) => d).sort();
  return {
    cuisineIds: [], feitos: r.corrida, ultimo: r.ultimo,
    // Quantos dias ESTA pessoa postou, e o último: é o que diz, na tela, quem ainda falta hoje.
    meus: diasDaPessoa.length,
    ultimoMeu: diasDaPessoa.at(-1) ?? "",
    ...fechado(event, membros.has(eu) ? r.fechou : ""),
  };
}

/**
 * Onde a pessoa está, para DESENHAR: quantos de quantos, e se a
 * sequência ainda está de pé hoje.
 */
export function progressoDaMissao(
  event: ChallengeEvent, estado: EventState, hoje: DayKey,
): { feitos: number; need: number; fechou: boolean; quebrou: boolean } {
  const need = Math.max(1, event.need);
  const fechou = !!estado.day;
  let feitos = Math.min(estado.feitos ?? estado.cuisineIds.length, need);
  let quebrou = false;
  const tipo = tipoDaMissao(event);
  if ((tipo === "sequencia" || tipo === "coletivaTodoDia") && !fechou && estado.ultimo) {
    // Viva se o último dia bom é hoje ou ontem (hoje ainda dá tempo).
    const viva = estado.ultimo === hoje || diaSeguinte(estado.ultimo) === hoje;
    if (!viva) { quebrou = true; feitos = 0; }
  }
  return { feitos: fechou ? need : feitos, need, fechou, quebrou };
}

/**
 * As missões de contagem ou sequência em que este prato CONTA, no dia
 * dele. Para a tela do prato dizer "este aqui ajudou na Semana leve".
 */
export function missoesQueOPratoAjuda(
  config: ChallengeConfig,
  plate: PratoParaCondicao & Pick<Plate, "day">,
): ChallengeEvent[] {
  return config.events.filter((e) => {
    if (!aberta(e, plate.day)) return false;
    if (tipoDaMissao(e) === "diaCompleto") {
      return (
        typeof plate.mealId === "string" &&
        (e.refeicoes ?? []).includes(plate.mealId) &&
        cumpreTodas(e.condicoes, plate)
      );
    }
    return usaCondicoes(e) && cumpreTodas(e.condicoes, plate);
  });
}

/** As refeições exigidas por esta missão, se houver (para dia completo ou condição de refeição). */
export function refeicoesDaMissao(e: ChallengeEvent): string[] {
  const tipo = tipoDaMissao(e);
  if (tipo === "diaCompleto") {
    return e.refeicoes ?? [];
  }
  const ref = (e.condicoes ?? []).find((c): c is Extract<CondicaoDeMissao, { tipo: "refeicao" }> => c.tipo === "refeicao");
  return ref?.mealId ? [ref.mealId] : [];
}

/** Quantos dias do calendário de `de` até `ate` (0 = mesmo dia). */
export function diasEntre(de: DayKey, ate: DayKey): number {
  const utc = (d: DayKey) => {
    const [y, m, dd] = d.split("-").map(Number);
    return Date.UTC(y ?? 1970, (m ?? 1) - 1, dd ?? 1);
  };
  return Math.round((utc(ate) - utc(de)) / 86_400_000);
}

/** Onde a missão está no calendário, visto de hoje. */
export function situacaoDaMissao(e: ChallengeEvent, hoje: DayKey): "breve" | "valendo" | "acabou" {
  if (e.from && hoje < e.from) return "breve";
  if (e.to && hoje > e.to) return "acabou";
  return "valendo";
}

/* ---------- as frases das telas ----------

   Aqui, e não em cada app: o site e o nativo dizem a mesma coisa com as
   mesmas palavras, e duas cópias de "faltam 2 pratos" divergem na
   primeira correção de português. */

const DIAS_DA_SEMANA = ["dom", "seg", "ter", "qua", "qui", "sex", "sáb"];
const MESES = ["jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"];

/** "dom, 28/set" */
export function diaCurto(dia: DayKey): string {
  const [a, m, d] = dia.split("-").map(Number);
  const data = new Date(Date.UTC(a ?? 1970, (m ?? 1) - 1, d ?? 1));
  return `${DIAS_DA_SEMANA[data.getUTCDay()]}, ${d}/${MESES[(m ?? 1) - 1]}`;
}

/** "até dom, 27/set", "termina hoje", "sem prazo", "começa qui, 2/out". */
export function prazoDaMissao(e: ChallengeEvent, hoje: DayKey): string {
  const situacao = situacaoDaMissao(e, hoje);
  if (situacao === "breve") return `começa ${diaCurto(e.from)}`;
  if (situacao === "acabou") return `acabou ${diaCurto(e.to)}`;
  if (!e.to) return "sem prazo";
  const n = diasEntre(hoje, e.to);
  if (n === 0) return "termina hoje";
  if (n === 1) return "termina amanhã";
  return `até ${diaCurto(e.to)}`;
}

const unidadeDaMissao = (e: ChallengeEvent, n: number) => {
  switch (tipoDaMissao(e)) {
    case "contagem": case "coletivaPratos": return n === 1 ? "prato" : "pratos";
    case "sequencia": case "diaCompleto": case "coletivaTodoDia": return n === 1 ? "dia" : "dias";
    case "lugares": return n === 1 ? "lugar" : "lugares";
    case "atividade":
      return e.acao === "comentar" ? (n === 1 ? "comentário" : "comentários") : (n === 1 ? "cravada" : "cravadas");
    default: return n === 1 ? "selo" : "selos";
  }
};

const SEM_ESTADO: EventState = { cuisineIds: [], bonus: 0, day: "" };

/** A linha curta de estado: "faltam 2 pratos", "✓ fechou · +3 pts". */
export function recadoDoProgresso(
  e: ChallengeEvent, estado: EventState | undefined, hoje: DayKey,
): string {
  const p = progressoDaMissao(e, estado ?? SEM_ESTADO, hoje);
  if (p.fechou) return `✓ fechou · +${pointsLabel(e.bonus)}`;
  if (p.quebrou) return "a sequência quebrou — recomeça no próximo prato";
  if (situacaoDaMissao(e, hoje) === "acabou") return "não fechou";
  const falta = p.need - p.feitos;
  if (tipoDaMissao(e) === "coletivaPratos") {
    return `grupo: ${p.feitos} de ${p.need} · você pôs ${estado?.meus ?? 0}`;
  }
  if (tipoDaMissao(e) === "coletivaTodoDia") {
    return p.feitos > 0
      ? `${p.feitos} ${p.feitos === 1 ? "dia" : "dias"} com todo mundo · falta${falta === 1 ? "" : "m"} ${falta}`
      : "ninguém pode furar — o grupo começa do zero";
  }
  if (tipoDaMissao(e) === "sequencia" && p.feitos > 0) {
    return `${p.feitos} ${p.feitos === 1 ? "dia" : "dias"} seguidos · falta${falta === 1 ? "" : "m"} ${falta}`;
  }
  return `falta${falta === 1 ? "" : "m"} ${falta} ${unidadeDaMissao(e, falta)}`;
}

/** O progresso com estado ausente tratado — o atalho das telas. */
export const progressoDe = (e: ChallengeEvent, estado: EventState | undefined, hoje: DayKey) =>
  progressoDaMissao(e, estado ?? SEM_ESTADO, hoje);

/* ---------- limpeza do que vem do banco ---------- */

/** Só as condições que fazem sentido; o resto some em vez de virar missão impossível ou de graça. */
export function condicoesLimpas(lista: unknown): CondicaoDeMissao[] {
  if (!Array.isArray(lista)) return [];
  return lista.flatMap((c): CondicaoDeMissao[] => {
    if (!c || typeof c !== "object") return [];
    const o = c as Record<string, unknown>;
    if (o["tipo"] === "kcalAte") {
      const kcal = Number(o["kcal"]);
      return Number.isFinite(kcal) && kcal > 0 ? [{ tipo: "kcalAte", kcal }] : [];
    }
    if (o["tipo"] === "sem") {
      const marca = marcasValidas([o["marca"]])?.[0];
      return marca ? [{ tipo: "sem", marca }] : [];
    }
    if (o["tipo"] === "cozinhado") return [{ tipo: "cozinhado" }];
    if (o["tipo"] === "comprado") return [{ tipo: "comprado" }];
    if (o["tipo"] === "lugarNovo") return [{ tipo: "lugarNovo" }];
    if (o["tipo"] === "refeicao" && typeof o["mealId"] === "string" && o["mealId"]) {
      return [{ tipo: "refeicao", mealId: o["mealId"] }];
    }
    if (o["tipo"] === "cozinha" && typeof o["cuisineId"] === "string" && o["cuisineId"]) {
      return [{ tipo: "cozinha", cuisineId: o["cuisineId"] }];
    }
    if (o["tipo"] === "precoAte") {
      const valor = Number(o["valor"]);
      return Number.isFinite(valor) && valor > 0 ? [{ tipo: "precoAte", valor }] : [];
    }
    if (o["tipo"] === "horaAntes" || o["tipo"] === "horaDepois") {
      const hora = Math.round(Number(o["hora"]));
      return Number.isFinite(hora) && hora >= 0 && hora <= 23 ? [{ tipo: o["tipo"], hora }] : [];
    }
    if (o["tipo"] === "notaMin") {
      const nota = Number(o["nota"]);
      return Number.isFinite(nota) && nota >= 1 && nota <= 10 ? [{ tipo: "notaMin", nota }] : [];
    }
    if (o["tipo"] === "canal" && Array.isArray(o["canais"])) {
      const canais = [...new Set(o["canais"].map(canalValido).filter((x): x is Canal => !!x))];
      return canais.length ? [{ tipo: "canal", canais }] : [];
    }
    return [];
  });
}

/* ---------- modelos prontos ---------- */

export interface ModeloDeMissao {
  id: string;
  name: string;
  emoji: string;
  tipo: TipoDeMissao;
  need: number;
  bonus: number;
  /** Quantos dias a janela dura a partir de hoje (0 = sem prazo). */
  dias: number;
  condicoes: CondicaoDeMissao[];
  refeicoes?: string[];
  acao?: AcaoDeMissao;
  /** Uma linha para quem escolhe. */
  resumo: string;
  /** Para agrupar na lista de modelos. */
  grupo: "Comer bem" | "Explorar" | "Bolso" | "Rotina" | "Com a galera" | "Todo mundo junto";
}

export const MODELOS_DE_MISSAO: readonly ModeloDeMissao[] = [
  { id: "semana-leve", grupo: "Comer bem", name: "Semana leve", emoji: "🥗", tipo: "contagem", need: 5, bonus: 3, dias: 7,
    condicoes: [{ tipo: "kcalAte", kcal: 600 }], resumo: "5 pratos de até 600 kcal na semana" },
  { id: "zero-acucar", grupo: "Comer bem", name: "Zero açúcar", emoji: "🍎", tipo: "contagem", need: 7, bonus: 3, dias: 7,
    condicoes: [{ tipo: "sem", marca: "acucar" }], resumo: "7 pratos sem açúcar na semana" },
  { id: "chef-da-semana", grupo: "Comer bem", name: "Chef da semana", emoji: "👨‍🍳", tipo: "contagem", need: 5, bonus: 2, dias: 7,
    condicoes: [{ tipo: "cozinhado" }], resumo: "5 pratos cozinhados na semana" },
  { id: "menos-carne", grupo: "Comer bem", name: "Menos carne", emoji: "🌱", tipo: "contagem", need: 4, bonus: 2, dias: 7,
    condicoes: [{ tipo: "sem", marca: "carne" }], resumo: "4 pratos sem carne na semana" },
  { id: "sem-fritura", grupo: "Comer bem", name: "5 dias sem fritura", emoji: "🍳", tipo: "sequencia", need: 5, bonus: 3, dias: 7,
    condicoes: [{ tipo: "sem", marca: "fritura" }], resumo: "5 dias seguidos com prato sem fritura" },

  { id: "passaporte", grupo: "Explorar", name: "Volta ao mundo", emoji: "🌍", tipo: "colecao", need: 3, bonus: 2, dias: 30,
    condicoes: [], resumo: "3 cozinhas diferentes de uma lista" },
  { id: "semana-japonesa", grupo: "Explorar", name: "Semana japonesa", emoji: "🍣", tipo: "contagem", need: 3, bonus: 2, dias: 7,
    condicoes: [{ tipo: "cozinha", cuisineId: "japonesa" }], resumo: "3 pratos japoneses na semana (troque a cozinha)" },
  { id: "turista", grupo: "Explorar", name: "Turista", emoji: "🗺️", tipo: "lugares", need: 5, bonus: 3, dias: 30,
    condicoes: [], resumo: "comer em 5 lugares diferentes no mês" },
  { id: "desbravador", grupo: "Explorar", name: "Desbravador", emoji: "📍", tipo: "contagem", need: 1, bonus: 2, dias: 30,
    condicoes: [{ tipo: "lugarNovo" }], resumo: "comer num lugar que ninguém do grupo marcou" },

  { id: "semana-economica", grupo: "Bolso", name: "Semana econômica", emoji: "💸", tipo: "contagem", need: 5, bonus: 2, dias: 7,
    condicoes: [{ tipo: "comprado" }, { tipo: "precoAte", valor: 25 }], resumo: "5 pratos comprados de até R$ 25" },
  { id: "sem-delivery", grupo: "Bolso", name: "Sem delivery", emoji: "🍽️", tipo: "contagem", need: 5, bonus: 2, dias: 7,
    condicoes: [{ tipo: "canal", canais: ["local", "retirada"] }], resumo: "5 pratos no local ou de retirada" },

  { id: "madrugador", grupo: "Rotina", name: "Madrugador", emoji: "🌅", tipo: "contagem", need: 5, bonus: 2, dias: 7,
    condicoes: [{ tipo: "refeicao", mealId: "cafe" }, { tipo: "horaAntes", hora: 8 }], resumo: "café antes das 8h, 5 dias" },
  { id: "dia-completo", grupo: "Rotina", name: "Dia completo", emoji: "🍱", tipo: "diaCompleto", need: 3, bonus: 3, dias: 7,
    condicoes: [], refeicoes: ["cafe", "almoco", "janta"], resumo: "café, almoço e janta no mesmo dia, 3 vezes" },
  { id: "cafe-todo-dia", grupo: "Rotina", name: "Café todo dia", emoji: "☕", tipo: "sequencia", need: 5, bonus: 2, dias: 7,
    condicoes: [{ tipo: "refeicao", mealId: "cafe" }], resumo: "5 dias seguidos postando o café" },
  { id: "larica", grupo: "Rotina", name: "Larica", emoji: "🦉", tipo: "contagem", need: 3, bonus: 1, dias: 7,
    condicoes: [{ tipo: "horaDepois", hora: 23 }], resumo: "3 pratos depois das 23h (é zoeira)" },

  { id: "vidente", grupo: "Com a galera", name: "Vidente", emoji: "🎯", tipo: "atividade", acao: "cravar", need: 3, bonus: 2, dias: 7,
    condicoes: [], resumo: "cravar 3 preços ou calorias na semana" },
  { id: "critico", grupo: "Com a galera", name: "Crítico", emoji: "💬", tipo: "atividade", acao: "comentar", need: 10, bonus: 2, dias: 7,
    condicoes: [], resumo: "comentar em 10 pratos dos outros" },
  { id: "masterchef", grupo: "Com a galera", name: "Masterchef", emoji: "⭐", tipo: "contagem", need: 3, bonus: 3, dias: 30,
    condicoes: [{ tipo: "notaMin", nota: 8 }], resumo: "3 pratos com nota média 8 ou mais" },

  { id: "mutirao", grupo: "Todo mundo junto", name: "Mutirão", emoji: "🏆", tipo: "coletivaPratos", need: 40, bonus: 2, dias: 7,
    condicoes: [], resumo: "o grupo posta 40 pratos na semana; quem contribuiu ganha" },
  { id: "ninguem-fura", grupo: "Todo mundo junto", name: "Ninguém fura", emoji: "🔥", tipo: "coletivaTodoDia", need: 5, bonus: 3, dias: 7,
    condicoes: [], resumo: "todo mundo posta todo dia por 5 dias; se um furar, ninguém ganha" },
];

/* ---------- o que as duas telas de regras dividem ---------- */

type TipoDeCondicao = CondicaoDeMissao["tipo"];

/** A condição deste tipo, se a missão tiver (as de "sem" podem ser várias: use `filter`). */
export function acharCondicao<T extends TipoDeCondicao>(
  condicoes: readonly CondicaoDeMissao[] | undefined, tipo: T,
): Extract<CondicaoDeMissao, { tipo: T }> | undefined {
  return (condicoes ?? []).find((c): c is Extract<CondicaoDeMissao, { tipo: T }> => c.tipo === tipo);
}

/** Troca a condição deste tipo por `nova` — ou tira, com `null`. */
export function trocarCondicao(
  condicoes: readonly CondicaoDeMissao[] | undefined, tipo: TipoDeCondicao, nova: CondicaoDeMissao | null,
): CondicaoDeMissao[] {
  const resto = (condicoes ?? []).filter((c) => c.tipo !== tipo);
  return nova ? [...resto, nova] : resto;
}

/** Liga ou desliga "sem <marca>", que pode aparecer mais de uma vez. */
export function alternarSem(
  condicoes: readonly CondicaoDeMissao[] | undefined, marca: Marca,
): CondicaoDeMissao[] {
  const lista = condicoes ?? [];
  const tem = lista.some((c) => c.tipo === "sem" && c.marca === marca);
  return tem
    ? lista.filter((c) => !(c.tipo === "sem" && c.marca === marca))
    : [...lista, { tipo: "sem", marca }];
}

/** Os tipos, na ordem e com o nome que a tela de regras mostra. */
export const TIPOS_DE_MISSAO: readonly { id: TipoDeMissao; nome: string; unidade: string }[] = [
  { id: "contagem", nome: "Pratos", unidade: "pratos" },
  { id: "sequencia", nome: "Dias seguidos", unidade: "dias seguidos" },
  { id: "lugares", nome: "Lugares diferentes", unidade: "lugares" },
  { id: "diaCompleto", nome: "Dia completo", unidade: "dias" },
  { id: "atividade", nome: "Com a galera", unidade: "vezes" },
  { id: "coletivaPratos", nome: "Grupo soma pratos", unidade: "pratos do grupo" },
  { id: "coletivaTodoDia", nome: "Ninguém fura", unidade: "dias seguidos" },
  { id: "colecao", nome: "Coleção de selos", unidade: "selos" },
];

/** Quantos dias sem postar tiram alguém do "todo mundo" das coletivas. */
export const DIAS_ATIVO_NA_COLETIVA = 7;

/* ---------- criar pela tela de Missões ---------- */

/** Os prazos que a tela oferece em botão. */
export const PRAZOS_DE_MISSAO = [
  { id: "1s", rotulo: "1 semana", dias: 7 },
  { id: "2s", rotulo: "2 semanas", dias: 14 },
  { id: "1m", rotulo: "1 mês", dias: 30 },
] as const;

/** A janela de quem começa `hoje` e dura `dias` (0 = sem prazo). */
export const janelaDoPrazo = (hoje: DayKey, dias: number): { from: DayKey; to: DayKey | "" } =>
  ({ from: hoje, to: dias ? somarDias(hoje, dias - 1) : "" });

/** O domingo da semana de `dia` (a semana vai de segunda a domingo). */
export function domingoDe(dia: DayKey): DayKey {
  const [a, m, d] = dia.split("-").map(Number);
  const dow = new Date(Date.UTC(a ?? 1970, (m ?? 1) - 1, d ?? 1)).getUTCDay();
  return somarDias(dia, (7 - dow) % 7);
}

/**
 * Liga "repetir toda semana": a janela vai de hoje até domingo, e a
 * partir daí renova toda segunda (`renovarMissoes`).
 */
export function comRepeticao(e: ChallengeEvent, hoje: DayKey, repetir: boolean): ChallengeEvent {
  const { repetir: _r, serie: _s, ...resto } = e;
  if (!repetir) return resto;
  return { ...resto, from: hoje, to: domingoDe(hoje), repetir: "semana", serie: e.serie ?? e.id };
}

/**
 * As missões depois da virada da semana: cada missão que se repete e já
 * acabou ganha a edição desta semana (segunda a domingo), com id novo, e
 * a velha deixa de repetir — vira histórico, com bônus e conquista
 * intactos. `null` = nada a renovar.
 */
export function renovarMissoes(events: readonly ChallengeEvent[], segunda: DayKey): ChallengeEvent[] | null {
  let mudou = false;
  const novas: ChallengeEvent[] = [];
  const lista = events.map((e) => {
    if (e.repetir !== "semana" || !e.to || e.to >= segunda) return e;
    mudou = true;
    const serie = e.serie ?? e.id;
    const id = `${serie}-${segunda}`;
    if (!events.some((x) => x.id === id)) {
      novas.push({ ...e, id, serie, from: segunda, to: somarDias(segunda, 6), repetir: "semana", fora: [] });
    }
    const { repetir: _r, ...velha } = e;
    return { ...velha, serie };
  });
  return mudou ? [...lista, ...novas] : null;
}

/**
 * Das missões encerradas de uma série, a tela mostra só a mais recente:
 * 52 "Semana leve" num ano é histórico, não lista.
 */
export function semRepeticoesVelhas(events: readonly ChallengeEvent[]): ChallengeEvent[] {
  const ultima = new Map<string, string>();
  for (const e of events) {
    if (!e.serie) continue;
    const atual = ultima.get(e.serie);
    if (!atual || (e.from || "") > atual) ultima.set(e.serie, e.from || "");
  }
  return events.filter((e) => !e.serie || ultima.get(e.serie) === (e.from || ""));
}

/** Os modelos que fazem sentido NESTE desafio: refeição e cozinha que ele tem. */
export function modelosDisponiveis(config: ChallengeConfig, interacao: Interacao = "todos"): ModeloDeMissao[] {
  const refeicoes = new Set(config.meals.map((m) => m.id));
  const cozinhas = new Set(config.cuisines.map((c) => c.id));
  return MODELOS_DE_MISSAO.filter((m) => missaoCabeNaInteracao(m, interacao) &&
    m.condicoes.every((c) =>
      (c.tipo !== "refeicao" || refeicoes.has(c.mealId)) && (c.tipo !== "cozinha" || cozinhas.has(c.cuisineId)))
    && (m.refeicoes ?? []).every((r) => refeicoes.has(r)));
}

function somarDias(dia: DayKey, n: number): DayKey {
  const [a, m, d] = dia.split("-").map(Number);
  return new Date(Date.UTC(a ?? 1970, (m ?? 1) - 1, (d ?? 1) + n)).toISOString().slice(0, 10);
}

/** O modelo vira missão, com a janela começando `hoje`. */
export function missaoDoModelo(m: ModeloDeMissao, id: string, hoje: DayKey): ChallengeEvent {
  const tipo = m.tipo;
  return {
    id, name: m.name, emoji: m.emoji,
    ...(tipo === "colecao" ? {} : { tipo }),
    ...(usaCondicoes({ tipo }) ? { condicoes: m.condicoes.map((c) => ({ ...c })) as CondicaoDeMissao[] } : {}),
    ...(m.refeicoes ? { refeicoes: [...m.refeicoes] } : {}),
    ...(m.acao ? { acao: m.acao } : {}),
    from: hoje, to: m.dias ? somarDias(hoje, m.dias - 1) : "",
    need: m.need, bonus: m.bonus, cuisineIds: [],
  };
}

/** Uma missão nova ao mudar de tipo: tira o que não vale mais e põe o que falta. */
export function mudarTipo(e: ChallengeEvent, tipo: TipoDeMissao): ChallengeEvent {
  const { tipo: _t, condicoes, refeicoes: _r, acao: _a, ...resto } = e;
  return {
    ...resto,
    ...(tipo === "colecao" ? {} : { tipo }),
    ...(usaCondicoes({ tipo }) ? { condicoes: condicoes ?? [] } : {}),
    ...(tipo === "diaCompleto" ? { refeicoes: e.refeicoes ?? [] } : {}),
    ...(tipo === "atividade" ? { acao: e.acao ?? "cravar" } : {}),
  };
}

/**
 * A linha de cada pessoa na lista "a galera" de uma missão. Nas coletivas
 * o número do grupo é um só, então a linha diz o que a PESSOA fez: quanto
 * pôs no mutirão, ou se já postou hoje no "ninguém fura" — que é o que
 * faz o grupo cobrar quem está faltando.
 */
export function linhaDaGalera(
  e: ChallengeEvent, estado: EventState | undefined, hoje: DayKey,
): { texto: string; ok: boolean } {
  const tipo = tipoDaMissao(e);
  if (tipo === "coletivaPratos") {
    if ((estado?.bonus ?? 0) > 0) return { texto: "✓ ganhou", ok: true };
    const meus = estado?.meus ?? 0;
    return { texto: meus ? `pôs ${meus}` : "ainda não pôs", ok: false };
  }
  if (tipo === "coletivaTodoDia") {
    if ((estado?.bonus ?? 0) > 0) return { texto: "✓ ganhou", ok: true };
    if (situacaoDaMissao(e, hoje) !== "valendo") return { texto: "—", ok: false };
    return estado?.ultimoMeu === hoje
      ? { texto: "✓ postou hoje", ok: true }
      : { texto: "falta hoje", ok: false };
  }
  const p = progressoDe(e, estado, hoje);
  return p.fechou ? { texto: "✓ fechou", ok: true } : { texto: `${p.feitos}/${p.need}`, ok: false };
}

/**
 * O "5 de 5 ativos" das coletivas de todo dia: quantos do grupo contam
 * hoje (postaram nos últimos 7 dias e não foram tirados da missão).
 */
export function ativosDaColetiva(
  e: ChallengeEvent, membros: readonly { uid: string; days?: readonly DayKey[] }[], hoje: DayKey,
): { ativos: number; total: number } {
  const fora = new Set(e.fora ?? []);
  const desde = somarDias(hoje, -(DIAS_ATIVO_NA_COLETIVA - 1));
  const dentro = membros.filter((m) => !fora.has(m.uid));
  return {
    ativos: dentro.filter((m) => (m.days ?? []).some((d) => d >= desde && d <= hoje)).length,
    total: dentro.length,
  };
}

/* ---------- quem pode comentar e dar nota ---------- */

/**
 * O que o dono escolheu no desafio. "dono": só ele comenta e dá nota (o
 * autor ainda responde no próprio prato). "ninguem": sem comentários nem
 * notas. Palpites de preço e caloria e reações valem SEMPRE — são o jogo,
 * não a conversa.
 */
export type Interacao = "todos" | "dono" | "ninguem";

export const OPCOES_DE_INTERACAO: { id: Interacao; rotulo: string; dica: string }[] = [
  { id: "todos", rotulo: "Todo mundo", dica: "Como sempre foi" },
  { id: "dono", rotulo: "Só o dono", dica: "Só você comenta e dá nota; quem postou ainda responde no próprio prato" },
  { id: "ninguem", rotulo: "Ninguém", dica: "Sem comentários nem notas. Palpites e reações continuam" },
];

export const interacaoDe = (d: { interacao?: unknown } | null | undefined): Interacao =>
  d?.interacao === "dono" || d?.interacao === "ninguem" ? d.interacao : "todos";

/** Missão que depende de comentário ou de nota não faz sentido sem eles. */
export function missaoCabeNaInteracao(
  e: Pick<ChallengeEvent, "tipo" | "acao" | "condicoes">, interacao: Interacao,
): boolean {
  if (interacao !== "ninguem") return true;
  if (e.tipo === "atividade" && e.acao === "comentar") return false;
  return !(e.condicoes ?? []).some((c) => c.tipo === "notaMin");
}

/**
 * Esta pessoa pode comentar / dar nota neste prato? A regra do Firestore
 * repete a mesma conta (ver `podeInteragir` em infra/firestore.rules).
 */
export function podeInteragir(
  p: { interacao: Interacao; dono: string; autor: string; eu: string; fechado: boolean; acao: "comentar" | "nota" },
): boolean {
  if (p.fechado || p.interacao === "ninguem") return false;
  if (p.interacao === "todos") return true;
  if (p.eu === p.dono) return true;
  return p.acao === "comentar" && p.eu === p.autor;
}
